// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import {
  createContext,
  type FC,
  type PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { enableMapSet, produce } from 'immer';
import { EMPTY_VALUE } from '@shared/ui-kit/utils/constants';
import { useConst } from '@shared/ui-kit/hooks/use-const';

enableMapSet();

/** Represents the value provided by the state context. */
export interface StateContextValue {
  state: Map<string, any>;
  setState: (key: string, value: SetStateAction<any>) => void;
  getState: (key: string) => any;
  clearState: () => void;
  clearStateValue: (key: string) => void;
}

export const StateContext = createContext(
  EMPTY_VALUE as unknown as StateContextValue,
);

/**
 * React hook to access the state context.
 *
 * @returns The current state context value.
 * @throws Will throw an error if the context is not found.
 */
export const useStateProvider = () => {
  const context = useContext(StateContext);

  if (context === (EMPTY_VALUE as unknown as StateContextValue))
    throw new Error(
      'could not find state context value; please ensure the component is wrapped in a <StateProvider>',
    );

  return context;
};

/**
 * React hook to retrieve a specific state value and its setter.
 *
 * @param key - The key for the state value.
 * @returns A tuple containing the state value and its setter function.
 */
export const useStateValue = <T,>(
  key = EMPTY_VALUE as unknown as string,
  initialValue?: T | (() => T),
): [T, Dispatch<SetStateAction<T>>] => {
  const context = useStateProvider();

  const defaultValue = useMemo(() => {
    return initialValue instanceof Function ? initialValue() : initialValue;
  }, [initialValue]);

  const state = useMemo(
    () => context.state.get(key) ?? defaultValue,
    [context.state, defaultValue, key],
  );

  const setState = useCallback(
    (value: SetStateAction<T>) => context.setState(key, value),
    [context, key],
  );

  const hasKey = useMemo(() => key === (EMPTY_VALUE as unknown as string), [key]);

  // istanbul ignore next
  const noopValue = useConst(() => [defaultValue ?? undefined, () => ({})] as any);

  return hasKey ? noopValue : [state, setState];
};

/**
 * State provider component that manages the state context.
 *
 * @param children - The child components to be wrapped by the provider.
 * @returns The StateContext.Provider component.
 */
export const StateProvider: FC<
  PropsWithChildren<{ entries?: readonly (readonly [string, any])[] }>
> = ({ children, entries }) => {
  const [state, updateState] = useState(() => new Map(entries));

  const setState = useCallback((key: string, value: SetStateAction<any>) => {
    updateState(prev =>
      produce(prev, draft => {
        const changes = typeof value === 'function' ? value(prev.get(key)) : value;

        draft.set(key, changes);
      }),
    );
  }, []);

  const getState = useCallback((key: string) => state.get(key), [state]);

  const clearState = useCallback(() => {
    updateState(new Map());
  }, []);

  const clearStateValue = useCallback((key: string) => {
    updateState(prev =>
      produce(prev, draft => {
        draft.delete(key);
      }),
    );
  }, []);

  const context = useMemo(
    () => ({
      state,
      setState,
      getState,
      clearState,
      clearStateValue,
    }),
    [clearState, clearStateValue, getState, setState, state],
  );

  return <StateContext.Provider value={context}>{children}</StateContext.Provider>;
};
